import { useQuery } from "@tanstack/react-query";

import { API_BASE_URL } from "@/config";
import { isPublicBuylistPage, type PublicBuylistPage } from "@/hooks/usePublicBuylist";
import {
  deletePublicBuylist,
  readPublicBuylist,
  savePublicBuylist,
} from "@/lib/shows/publicBuylistCache";
import { validatedJson } from "@/lib/ui/validatedJson";

export async function loadTableBuylist(
  slug: string,
): Promise<{ page: PublicBuylistPage; savedAt: string | null; } | null> {
  try {
    const cards: PublicBuylistPage["cards"] = [];
    let cursor: string | null = null;
    let first: PublicBuylistPage | null = null;
    do {
      const query: string = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const response: Response = await fetch(
        `${API_BASE_URL}/shops/${encodeURIComponent(slug)}/buylist${query}`,
      );
      if (response.status === 404) {
        await deletePublicBuylist(slug);
        return null;
      }
      if (!response.ok) throw new Error("Buylist unavailable");
      const page: PublicBuylistPage = await validatedJson(
        response,
        isPublicBuylistPage,
        "Buylist was invalid",
      );
      first ??= page;
      cards.push(...page.cards);
      cursor = page.next_cursor;
    } while (cursor);
    const page = { ...first, cards, next_cursor: null };
    await savePublicBuylist(slug, page);
    return { page, savedAt: null };
  } catch (error) {
    const cached = await readPublicBuylist(slug);
    if (cached) return cached;
    throw error;
  }
}

export function useTableBuylist(slug: string | null | undefined) {
  return useQuery({
    queryKey: ["table-buylist", slug],
    enabled: !!slug,
    retry: false,
    queryFn: () => loadTableBuylist(slug!),
  });
}
